import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../../utils/animations';
import { Eyebrow, PageHeader, Panel } from '../../ui/kit';

const CheckIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);

const ExclamationCircleIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
  </svg>
);

interface SettingsData {
  profile: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    createdAt: string;
  };
  preferences: {
    timezone: string;
    emailNotifications: boolean;
    pushNotifications: boolean;
    weeklyDigest: boolean;
    activityNotifications?: boolean;
    leadNotifications?: boolean;
    reviewNotifications?: boolean;
    bookingNotifications?: boolean;
  };
}

const NotificationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch settings
  const { data: settings, isLoading } = useQuery<SettingsData>({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await axios.get('/api/settings');
      return response.data.data;
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (data: Partial<SettingsData['preferences']>) => {
      const response = await axios.patch('/api/settings', data);
      return response.data;
    },
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Notification preferences saved!' });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: () => {
      setMessage({ type: 'error', text: 'Failed to update notifications.' });
      // Refetch to reset UI to actual state
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setTimeout(() => setMessage(null), 3000);
    },
  });

  const handleToggle = (key: 'emailNotifications' | 'pushNotifications' | 'weeklyDigest' | 'activityNotifications' | 'leadNotifications' | 'reviewNotifications' | 'bookingNotifications') => {
    if (!settings) return;

    const currentValue = settings.preferences[key] ?? true;
    updateSettingsMutation.mutate({ [key]: !currentValue });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-40 bg-zinc-200 rounded animate-pulse" />
        <Panel className="p-6">
          <div className="space-y-6 max-w-lg">
            <div className="h-16 bg-zinc-200 rounded animate-pulse" />
            <div className="h-16 bg-zinc-200 rounded animate-pulse" />
            <div className="h-16 bg-zinc-200 rounded animate-pulse" />
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      <motion.div variants={staggerItem} initial="initial" animate="animate">
        <PageHeader
          eyebrow="Account"
          title="Notifications"
          description="Choose how you want to be notified"
        />
      </motion.div>

      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className={`flex items-center gap-3 p-4 rounded-xl ${
              message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckIcon className="w-5 h-5 text-green-600" />
            ) : (
              <ExclamationCircleIcon className="w-5 h-5 text-red-600" />
            )}
            <span className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/*
        Toggles left, the explanation right — the notes are reference material
        for the switch you're looking at, not a footer to scroll past.
      */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,34rem)_18rem] lg:items-start">
      <motion.div variants={staggerItem} initial="initial" animate="animate">
        <Panel className="p-6">
        <div className="space-y-8">
          {/* Email Notifications */}
          <div>
            <Eyebrow className="mb-4">Email Notifications</Eyebrow>
            <div className="space-y-4">
              <NotificationToggle
                label="Email Notifications"
                description="Receive important updates and alerts via email"
                checked={settings?.preferences.emailNotifications ?? true}
                onChange={() => handleToggle('emailNotifications')}
                disabled={updateSettingsMutation.isPending}
              />
              <NotificationToggle
                label="Weekly Digest"
                description="Get a weekly summary of your website performance"
                checked={settings?.preferences.weeklyDigest ?? true}
                onChange={() => handleToggle('weeklyDigest')}
                disabled={updateSettingsMutation.isPending}
              />
            </div>
          </div>

          {/* Push Notifications */}
          <div className="pt-6 border-t border-zinc-100">
            <Eyebrow className="mb-4">Push Notifications</Eyebrow>
            <div className="space-y-4">
              <NotificationToggle
                label="Browser Push Notifications"
                description="Get instant notifications in your browser"
                checked={settings?.preferences.pushNotifications ?? true}
                onChange={() => handleToggle('pushNotifications')}
                disabled={updateSettingsMutation.isPending}
              />
            </div>
          </div>

          {/* Activity Notifications */}
          <div className="pt-6 border-t border-zinc-100">
            <Eyebrow className="mb-4">Activity Notifications</Eyebrow>
            <div className="space-y-4">
              <NotificationToggle
                label="Activity Alerts"
                description="Get notified about important account activities and updates"
                checked={settings?.preferences.activityNotifications ?? true}
                onChange={() => handleToggle('activityNotifications')}
                disabled={updateSettingsMutation.isPending}
              />
              <NotificationToggle
                label="Lead Notifications"
                description="Receive alerts when new leads are captured"
                checked={settings?.preferences.leadNotifications ?? true}
                onChange={() => handleToggle('leadNotifications')}
                disabled={updateSettingsMutation.isPending}
              />
              <NotificationToggle
                label="Review Notifications"
                description="Get notified when new reviews are posted"
                checked={settings?.preferences.reviewNotifications ?? true}
                onChange={() => handleToggle('reviewNotifications')}
                disabled={updateSettingsMutation.isPending}
              />
              <NotificationToggle
                label="Booking Notifications"
                description="Receive alerts for new bookings and appointment requests"
                checked={settings?.preferences.bookingNotifications ?? true}
                onChange={() => handleToggle('bookingNotifications')}
                disabled={updateSettingsMutation.isPending}
              />
            </div>
          </div>
        </div>
        </Panel>
      </motion.div>

      {/* Info Card — sticky beside the toggles */}
      <motion.div variants={staggerItem} initial="initial" animate="animate" className="lg:sticky lg:top-6">
        <Panel className="p-5">
        <Eyebrow className="mb-3">About Notifications</Eyebrow>
        <ul className="space-y-2 text-[13.5px] leading-relaxed text-muted">
          <li>- Email notifications include lead alerts, billing reminders, and important updates</li>
          <li>- Weekly digest summarizes your website views, leads, and reviews</li>
          <li>- Push notifications require browser permission and only work when the browser is open</li>
          <li>- Activity notifications keep you informed about leads, reviews, and bookings in real-time</li>
          <li>- You can customize which activities trigger notifications to reduce noise</li>
        </ul>
        </Panel>
      </motion.div>
      </div>
    </motion.div>
  );
};

// Notification Toggle Component
interface NotificationToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}

const NotificationToggle: React.FC<NotificationToggleProps> = ({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}) => (
  <motion.label
    className={`flex items-start gap-4 cursor-pointer group ${disabled ? 'opacity-50' : ''}`}
    whileHover={disabled ? {} : { x: 2 }}
    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
  >
    <div className="relative mt-0.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only peer"
      />
      <motion.div
        className={`w-11 h-6 rounded-full ${
          !checked ? 'bg-zinc-300' : ''
        }`}
        style={{ backgroundColor: checked ? 'var(--primary)' : undefined }}
      />
      <motion.div
        animate={{ x: checked ? 20 : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full"
      />
    </div>
    <div className="flex-1">
      <p className="text-[15px] font-medium text-primary transition-colors">
        {label}
      </p>
      <p className="text-[13px] text-muted">{description}</p>
    </div>
  </motion.label>
);

export default NotificationsPage;
